/**
 * Iconos de la interfaz: SVG inline dibujados para esta app.
 *
 * Sin libreria de iconos a proposito: son una docena, y asi heredan el color
 * del texto (`currentColor`) y el grosor de trazo de todo el resto, en los dos
 * temas. Son decorativos (`aria-hidden`): el nombre accesible lo pone siempre
 * el texto del control que los lleva.
 */
import type { ReactElement, ReactNode } from 'react';

export interface IconProps {
  /** Lado en pixeles. Por defecto 18, el tamano del menu. */
  readonly size?: number;
  readonly className?: string;
}

function Icon({
  size = 18,
  className,
  children,
}: IconProps & { readonly children: ReactNode }): ReactElement {
  return (
    <svg
      className={className === undefined ? 'icon' : `icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Tablero: cuatro paneles de distinto alto. */
export function IconDashboard(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3.5" y="3.5" width="7" height="9" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
      <rect x="3.5" y="15.5" width="7" height="5" rx="1.5" />
    </Icon>
  );
}

/** Mensajes: la bandeja por donde pasa todo lo que entra y sale. */
export function IconMessages(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M3.5 13h4.25l1.5 2.5h5.5l1.5-2.5h4.25" />
      <path d="M6.3 5h11.4a1.5 1.5 0 0 1 1.42 1.02L20.5 13v5.5A1.5 1.5 0 0 1 19 20H5a1.5 1.5 0 0 1-1.5-1.5V13l1.38-6.98A1.5 1.5 0 0 1 6.3 5Z" />
    </Icon>
  );
}

/** Corridas: ejecuciones apiladas en el tiempo, cada una con su estado. */
export function IconRuns(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="5.5" cy="6" r="1.75" />
      <circle cx="5.5" cy="12" r="1.75" />
      <circle cx="5.5" cy="18" r="1.75" />
      <path d="M10 6h10.5" />
      <path d="M10 12h10.5" />
      <path d="M10 18h7" />
    </Icon>
  );
}

/** Dead letters: un sobre que no llego a destino. */
export function IconDeadLetter(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M20.5 11V6.5A1.5 1.5 0 0 0 19 5H5a1.5 1.5 0 0 0-1.5 1.5v10A1.5 1.5 0 0 0 5 18h7" />
      <path d="m3.75 6.25 8.25 6 8.25-6" />
      <circle cx="18" cy="17.5" r="3.5" />
      <path d="m16.6 16.1 2.8 2.8m0-2.8-2.8 2.8" />
    </Icon>
  );
}

/** Cuarentena: retenido para revisar. */
export function IconQuarantine(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M12 3.25 5 5.9v5.35c0 4.3 2.95 7.75 7 9.5 4.05-1.75 7-5.2 7-9.5V5.9l-7-2.65Z" />
      <path d="M12 8.25v4.5" />
      <path d="M12 16.1v.01" />
    </Icon>
  );
}

/** Procesos: pasos encadenados. */
export function IconProcesses(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="3.5" width="7" height="5" rx="1.5" />
      <rect x="14" y="9.5" width="7" height="5" rx="1.5" />
      <rect x="3" y="15.5" width="7" height="5" rx="1.5" />
      <path d="M10 6h2.5a2 2 0 0 1 2 2v1.5" />
      <path d="M14.5 14.5V16a2 2 0 0 1-2 2H10" />
    </Icon>
  );
}

/** Bodegas: un galpon con su porton. */
export function IconWarehouse(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M3.5 20V9l8.5-5 8.5 5v11" />
      <path d="M7.5 20v-7.5h9V20" />
      <path d="M7.5 15.5h9" />
    </Icon>
  );
}

export function IconSun(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3.75" />
      <path d="M12 2.75v1.75M12 19.5v1.75M4.4 4.4l1.25 1.25M18.35 18.35l1.25 1.25M2.75 12H4.5M19.5 12h1.75M4.4 19.6l1.25-1.25M18.35 5.65l1.25-1.25" />
    </Icon>
  );
}

export function IconMoon(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M19.5 14.6A7.75 7.75 0 0 1 9.4 4.5a7.75 7.75 0 1 0 10.1 10.1Z" />
    </Icon>
  );
}

/** Tema del sistema: la pantalla del equipo. */
export function IconMonitor(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="12" rx="1.75" />
      <path d="M8.5 20h7M12 16v4" />
    </Icon>
  );
}

export function IconMenu(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    </Icon>
  );
}

export function IconClose(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="m6 6 12 12M18 6 6 18" />
    </Icon>
  );
}

/** Contraer el menu: el panel lateral con la flecha hacia adentro. */
export function IconSidebarCollapse(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="m15.5 9.5-2.5 2.5 2.5 2.5" />
    </Icon>
  );
}

export function IconSidebarExpand(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
      <path d="m13 9.5 2.5 2.5-2.5 2.5" />
    </Icon>
  );
}

export function IconLogout(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M9.5 20h-4A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4h4" />
      <path d="m15.5 16 4-4-4-4" />
      <path d="M19.5 12h-10" />
    </Icon>
  );
}

export function IconChevronRight(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="m9.5 6 6 6-6 6" />
    </Icon>
  );
}

/** Embudo: el acceso a los filtros de un listado. */
export function IconFilter(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M4 5.5h16l-6.25 7.25v5.5l-3.5 1.75v-7.25z" />
    </Icon>
  );
}

/** Todo en orden. */
export function IconCheckCircle(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.75" />
      <path d="m8.25 12.25 2.5 2.5 5-5.25" />
    </Icon>
  );
}

/** Libro abierto: el mismo signo en todos los accesos a la guía de un flujo. */
export function IconBook(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <path d="M12 6.5C10.4 5.2 8 4.5 3 4.5v13c5 0 7.4.7 9 2 1.6-1.3 4-2 9-2v-13c-5 0-7.4.7-9 2z" />
      <path d="M12 6.5v13" />
    </Icon>
  );
}

/** Dibujo horizontal: tres pasos en fila. Dice la forma, no hacia dónde van las flechas. */
export function IconLayoutHorizontal(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="2.5" y="8.5" width="5" height="7" rx="1.25" />
      <rect x="9.5" y="8.5" width="5" height="7" rx="1.25" />
      <rect x="16.5" y="8.5" width="5" height="7" rx="1.25" />
    </Icon>
  );
}

/** Dibujo vertical: tres pasos en columna. */
export function IconLayoutVertical(props: IconProps): ReactElement {
  return (
    <Icon {...props}>
      <rect x="8.5" y="2.5" width="7" height="5" rx="1.25" />
      <rect x="8.5" y="9.5" width="7" height="5" rx="1.25" />
      <rect x="8.5" y="16.5" width="7" height="5" rx="1.25" />
    </Icon>
  );
}
